const LOCK_PREFIX = 'keilog-sso.auth-cookie:';
const SHARED_GENERATION_PREFIX = 'keilog-sso.auth-cookie-generation:';
const STABLE_PATTERN = /^(?:initial|v1:[A-Za-z0-9:._-]{1,180})$/;
const BUSY_PATTERN = /^busy:v1:(\d{10,16}):[A-Za-z0-9:._-]{1,180}$/;

function browserLocks() {
	return typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function'
		? navigator.locks
		: null;
}

function browserStorage() {
	try {
		return typeof window === 'undefined' ? null : window.localStorage;
	} catch (error) {
		return null;
	}
}

function generationKey(contextKey) {
	return `${SHARED_GENERATION_PREFIX}${encodeURIComponent(contextKey)}`;
}

function busyIsStale(value, busyTtlMs, now = Date.now()) {
	const match = BUSY_PATTERN.exec(value);
	if (!match) return true;
	const writtenAt = Number(match[1]);
	return !Number.isFinite(writtenAt) || writtenAt > now || now - writtenAt > busyTtlMs;
}

function readSharedGeneration(storage, contextKey, busyTtlMs) {
	let value;
	try {
		value = storage.getItem(generationKey(contextKey));
	} catch (error) {
		throw new Error('SSO auth coordination storage is unavailable.');
	}

	if (value === null) return 'initial';
	if (BUSY_PATTERN.test(value)) {
		if (busyIsStale(value, busyTtlMs)) return 'initial';
		throw new Error('SSO auth mutation is busy.');
	}
	if (!STABLE_PATTERN.test(value)) {
		throw new Error('SSO auth coordination state is invalid.');
	}
	return value;
}

function randomValue() {
	if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
		return globalThis.crypto.randomUUID();
	}
	return `${Date.now()}:${Math.random().toString(36).slice(2)}`;
}

function writeBusy(storage, contextKey) {
	storage.setItem(generationKey(contextKey), `busy:v1:${Date.now()}:${randomValue()}`);
}

function writeStable(storage, contextKey) {
	storage.setItem(generationKey(contextKey), `v1:${randomValue()}`);
}

function isAuthMutationSupported(contextKey, busyTtlMs, locks = browserLocks(), storage = browserStorage()) {
	if (!locks || !storage) return false;
	try {
		readSharedGeneration(storage, contextKey, busyTtlMs);
		return true;
	} catch (error) {
		return error instanceof Error && error.message === 'SSO auth mutation is busy.';
	}
}

function createAuthMutationCoordinator({
	enabled,
	contextKey,
	busyTtlMs,
	getAuthGeneration,
	locks = browserLocks(),
	storage = browserStorage(),
}) {
	return {
		async run(operation, canRunAfterQueuedMutation) {
			if (!enabled) {
				return { outcome: 'applied', value: await operation() };
			}
			if (!locks || !storage) {
				return { outcome: 'unavailable' };
			}

			let expectedShared;
			let expectedAuth;
			try {
				expectedShared = readSharedGeneration(storage, contextKey, busyTtlMs);
				expectedAuth = getAuthGeneration();
			} catch (error) {
				if (!(error instanceof Error) || error.message !== 'SSO auth mutation is busy.') {
					return { outcome: 'unavailable' };
				}
				// Another tab already owns the exact Web Lock. Queue behind it;
				// once acquired, the changed stable generation makes this intent
				// stale and lets the caller adopt the winner's local session.
				expectedShared = null;
				expectedAuth = getAuthGeneration();
			}

			return locks.request(`${LOCK_PREFIX}${encodeURIComponent(contextKey)}`, { mode: 'exclusive' }, async () => {
				let currentShared;
				try {
					currentShared = readSharedGeneration(storage, contextKey, busyTtlMs);
				} catch (error) {
					return { outcome: 'unavailable' };
				}

				const generationMatches = getAuthGeneration() === expectedAuth;
				const sharedMatches = expectedShared !== null && currentShared === expectedShared;
				if ((!generationMatches || !sharedMatches) && !(canRunAfterQueuedMutation && canRunAfterQueuedMutation())) {
					return { outcome: 'stale' };
				}

				try {
					writeBusy(storage, contextKey);
				} catch (error) {
					return { outcome: 'unavailable' };
				}

				let value;
				try {
					value = await operation();
				} finally {
					writeStable(storage, contextKey);
				}
				return { outcome: 'applied', value };
			});
		},
	};
}

module.exports = {
	busyIsStale,
	createAuthMutationCoordinator,
	isAuthMutationSupported,
	readSharedGeneration,
};
